/** @format */

import React, { useState } from "react";
import { ReactComponent as Trash } from "bootstrap-icons/icons/trash.svg";
import { useTranslation } from "react-i18next";
import { Button, Modal, Spinner } from "react-bootstrap";
import { LocalText } from "../../resources/BLResourceTypes";
import { useLocale } from "../../../i18n/hooks";
import { useDeleteUserResourceMutation } from "../../../app/services/userResourceApi";
import { UserResourceReference } from "../../../types";
import HelpLink from "../../helpLink/HelpLink";

type Props = {
  resource: UserResourceReference;
};

const DeleteUserResource: React.FC<Props> = ({ resource }) => {
  const { t: _ } = useTranslation();
  const locale = useLocale();

  const [showModal, setShowModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [deleteUserResource] = useDeleteUserResourceMutation();

  const instructions: LocalText[] = [
    {
      en: `Are you sure you want to delete this user resource?`,
      cy: `CY DELETE user resource?`,
    },
    {
      en: "This action cannot be undone",
      cy: "Cannot Undo CY",
    },
  ];

  const removeResource = async () => {
    const code = resource.Code;
    const copy = resource.Copy;
    const locale = resource.Locale;

    if (copy === undefined) return;

    await deleteUserResource({ code, copy, locale });
    setIsLoading(false);
  };

  return (
    <>
      <button
        title={_("Delete Resource?")}
        className="action"
        onClick={() => setShowModal(true)}
      >
        <Trash className="icon" />
      </button>
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        aria-label="delete-user-resource-modal"
        centered
      >
        <Modal.Header closeButton>
          <span className="flex-grow-1">{_("Delete this User Resource?")}</span>
          <HelpLink position="inherit" className="p-0 help-link" />
        </Modal.Header>
        <Modal.Body>
          <p>
            {instructions[0][locale]}
            <br />
            <strong>{instructions[1][locale]}</strong>
          </p>
          <div className="d-flex gap-3 justify-content-end">
            <Button
              role="button"
              variant="outline-editing"
              onClick={() => setShowModal(false)}
            >
              {_("Cancel")}
            </Button>
            <Button
              disabled={isLoading}
              role="button"
              variant="danger"
              onClick={async () => {
                setIsLoading(true);
                await removeResource();
                setShowModal(false);
              }}
            >
              {isLoading ? <Spinner /> : _("Delete")}
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default DeleteUserResource;
